// Input glyph badges drawn in-game: keyboard keycaps ([E], [ESC], [SHIFT]),
// mouse buttons, and gamepad buttons (face letters, bumpers, triggers, d-pad,
// sticks, start/back). ActionHint / HintBar swap automatically between the
// keyboard+mouse and gamepad glyph sets when the player changes device.

import Phaser from 'phaser';
import { FONT } from '../config/constants';
import { COLORS, css } from '../config/palette';
import { getInputMode, onInputModeChange, type InputMode } from './nav';

export type MouseGlyph = 'LMB' | 'RMB' | 'MMB' | 'WHEEL' | 'MOUSE';
export type PadGlyph =
  | 'PAD_A'
  | 'PAD_B'
  | 'PAD_X'
  | 'PAD_Y'
  | 'PAD_LB'
  | 'PAD_RB'
  | 'PAD_LT'
  | 'PAD_RT'
  | 'PAD_START'
  | 'PAD_BACK'
  | 'PAD_DPAD'
  | 'PAD_DPAD_H'
  | 'PAD_DPAD_V'
  | 'PAD_DUP'
  | 'PAD_DDOWN'
  | 'PAD_DLEFT'
  | 'PAD_DRIGHT'
  | 'PAD_LS'
  | 'PAD_RS';
/** Any other string is drawn as a keyboard keycap with that label ('E', 'ESC', '←→'). */
export type GlyphId = MouseGlyph | PadGlyph | (string & {});

/** Semantic UI actions; resolved to glyphs for the current input device. */
export type UiAction =
  | 'confirm'
  | 'back'
  | 'interact'
  | 'salvage'
  | 'signature'
  | 'ultimate'
  | 'dash'
  | 'fire'
  | 'jump'
  | 'switch'
  | 'weapon1'
  | 'weapon2'
  | 'map'
  | 'pause'
  | 'move'
  | 'aim'
  | 'navigate'
  | 'navigateH'
  | 'navigateV'
  | 'prev'
  | 'next'
  | 'tab'
  | 'alt'
  | 'skip'
  | 'launch';

const KBM_GLYPHS: Record<UiAction, GlyphId[]> = {
  confirm: ['ENTER'],
  back: ['ESC'],
  interact: ['E'],
  salvage: ['X'],
  signature: ['RMB'],
  ultimate: ['Q'],
  dash: ['SHIFT'],
  fire: ['LMB'],
  jump: ['SPACE'],
  switch: ['WHEEL'],
  weapon1: ['1'],
  weapon2: ['2'],
  map: ['TAB'],
  pause: ['ESC'],
  move: ['A', 'D'],
  aim: ['MOUSE'],
  navigate: ['↑↓←→'],
  navigateH: ['← →'],
  navigateV: ['↑ ↓'],
  prev: ['Q'],
  next: ['E'],
  tab: ['TAB'],
  alt: ['X'],
  skip: ['ESC'],
  launch: ['ENTER'],
};

const PAD_GLYPHS: Record<UiAction, GlyphId[]> = {
  confirm: ['PAD_A'],
  back: ['PAD_B'],
  interact: ['PAD_X'],
  salvage: ['PAD_DDOWN'],
  signature: ['PAD_LT'],
  ultimate: ['PAD_Y'],
  dash: ['PAD_RB'],
  fire: ['PAD_RT'],
  jump: ['PAD_A'],
  switch: ['PAD_LB'],
  weapon1: ['PAD_LB'],
  weapon2: ['PAD_LB'],
  map: ['PAD_BACK'],
  pause: ['PAD_START'],
  move: ['PAD_LS'],
  aim: ['PAD_RS'],
  navigate: ['PAD_DPAD'],
  navigateH: ['PAD_DPAD_H'],
  navigateV: ['PAD_DPAD_V'],
  prev: ['PAD_LB'],
  next: ['PAD_RB'],
  tab: ['PAD_RT'],
  alt: ['PAD_X'],
  skip: ['PAD_B'],
  launch: ['PAD_START'],
};

export function glyphsFor(action: UiAction, mode: InputMode = getInputMode()): GlyphId[] {
  return (mode === 'pad' ? PAD_GLYPHS : KBM_GLYPHS)[action];
}

const PAD_FACE: Record<string, { color: number; letter: string }> = {
  PAD_A: { color: 0x5ccf52, letter: 'A' },
  PAD_B: { color: 0xec4b55, letter: 'B' },
  PAD_X: { color: 0x3f93f2, letter: 'X' },
  PAD_Y: { color: 0xf3bd32, letter: 'Y' },
};

const KEY_FILL = 0x1a222e;
const KEY_EDGE = 0x8796ab;
const INK = COLORS.ink;

function label(scene: Phaser.Scene, s: string, size: number, color: number | string, font: string = FONT.display): Phaser.GameObjects.Text {
  return scene.add
    .text(0, 0, s, {
      fontFamily: font,
      fontSize: `${Math.round(size)}px`,
      fontStyle: '700',
      color: typeof color === 'number' ? css(color) : color,
    })
    .setOrigin(0.5, 0.5);
}

/** Rounded rectangle path (Graphics has fillRoundedRect but we want consistent stroke widths). */
function roundRect(g: Phaser.GameObjects.Graphics, x: number, y: number, w: number, h: number, r: number, fill?: { c: number; a: number }, stroke?: { w: number; c: number; a: number }): void {
  if (fill) {
    g.fillStyle(fill.c, fill.a);
    g.fillRoundedRect(x, y, w, h, Math.min(r, w / 2, h / 2));
  }
  if (stroke) {
    g.lineStyle(stroke.w, stroke.c, stroke.a);
    g.strokeRoundedRect(x, y, w, h, Math.min(r, w / 2, h / 2));
  }
}

/**
 * Draw one glyph centred on (0,0) inside a container. `size` is the badge height in px.
 * The container's `width` is set to the glyph's drawn width (used for layout).
 */
export function createGlyph(scene: Phaser.Scene, id: GlyphId, size = 30): Phaser.GameObjects.Container {
  const c = scene.add.container(0, 0);
  const g = scene.add.graphics();
  c.add(g);
  const h = size;
  let w = h;

  const face = PAD_FACE[id];
  if (face) {
    const r = h / 2;
    g.fillStyle(face.color, 1);
    g.fillCircle(0, 0, r);
    g.lineStyle(Math.max(1.5, h * 0.06), 0xffffff, 0.35);
    g.strokeCircle(0, 0, r - h * 0.08);
    c.add(label(scene, face.letter, h * 0.56, INK));
    w = h;
  } else if (id === 'PAD_LB' || id === 'PAD_RB') {
    w = h * 1.45;
    const bh = h * 0.74;
    const left = id === 'PAD_LB';
    // Bumper: a bar whose outer top corner is strongly rounded.
    const r = left ? { tl: bh * 0.7, tr: 4, bl: 4, br: 4 } : { tl: 4, tr: bh * 0.7, bl: 4, br: 4 };
    g.fillStyle(0x2a3442, 1);
    g.fillRoundedRect(-w / 2, -bh / 2, w, bh, r);
    g.lineStyle(1.5, KEY_EDGE, 1);
    g.strokeRoundedRect(-w / 2, -bh / 2, w, bh, r);
    c.add(label(scene, left ? 'LB' : 'RB', h * 0.4, COLORS.uiText).setY(1));
  } else if (id === 'PAD_LT' || id === 'PAD_RT') {
    w = h * 1.15;
    roundRect(g, -w / 2, -h / 2, w, h, h * 0.42, { c: 0x2a3442, a: 1 }, { w: 1.5, c: KEY_EDGE, a: 1 });
    g.fillStyle(KEY_EDGE, 0.25);
    g.fillRoundedRect(-w / 2 + 3, h * 0.18, w - 6, h * 0.24, 3);
    c.add(label(scene, id === 'PAD_LT' ? 'LT' : 'RT', h * 0.4, COLORS.uiText).setY(-h * 0.08));
  } else if (id === 'PAD_START' || id === 'PAD_BACK') {
    w = h * 1.1;
    const ph = h * 0.74;
    roundRect(g, -w / 2, -ph / 2, w, ph, ph / 2, { c: 0x2a3442, a: 1 }, { w: 1.5, c: KEY_EDGE, a: 1 });
    g.lineStyle(2, COLORS.uiText, 1);
    if (id === 'PAD_START') {
      for (let i = -1; i <= 1; i++) g.lineBetween(-h * 0.2, i * h * 0.13, h * 0.2, i * h * 0.13);
    } else {
      g.strokeRect(-h * 0.2, -h * 0.17, h * 0.26, h * 0.22);
      g.fillStyle(COLORS.uiText, 1);
      g.fillRect(-h * 0.06, -h * 0.05, h * 0.26, h * 0.22);
    }
  } else if (id.startsWith('PAD_D')) {
    const arm = h * 0.34;
    const t = h * 0.3;
    const lit = {
      up: id === 'PAD_DPAD' || id === 'PAD_DPAD_V' || id === 'PAD_DUP',
      down: id === 'PAD_DPAD' || id === 'PAD_DPAD_V' || id === 'PAD_DDOWN',
      left: id === 'PAD_DPAD' || id === 'PAD_DPAD_H' || id === 'PAD_DLEFT',
      right: id === 'PAD_DPAD' || id === 'PAD_DPAD_H' || id === 'PAD_DRIGHT',
    };
    const armRect = (dx: number, dy: number, on: boolean): void => {
      g.fillStyle(on ? COLORS.uiText : 0x3a4656, 1);
      const x = dx === 0 ? -t / 2 : dx < 0 ? -t / 2 - arm : t / 2;
      const y = dy === 0 ? -t / 2 : dy < 0 ? -t / 2 - arm : t / 2;
      g.fillRect(x, y, dx === 0 ? t : arm, dy === 0 ? t : arm);
    };
    g.fillStyle(0x3a4656, 1);
    g.fillRect(-t / 2, -t / 2, t, t);
    armRect(0, -1, lit.up);
    armRect(0, 1, lit.down);
    armRect(-1, 0, lit.left);
    armRect(1, 0, lit.right);
    w = h;
  } else if (id === 'PAD_LS' || id === 'PAD_RS') {
    g.fillStyle(0x2a3442, 1);
    g.fillCircle(0, 0, h / 2);
    g.lineStyle(1.5, KEY_EDGE, 1);
    g.strokeCircle(0, 0, h / 2);
    g.fillStyle(0x465365, 1);
    g.fillCircle(0, 0, h * 0.3);
    c.add(label(scene, id === 'PAD_LS' ? 'L' : 'R', h * 0.36, COLORS.uiText));
    w = h;
  } else if (id === 'LMB' || id === 'RMB' || id === 'MMB' || id === 'WHEEL' || id === 'MOUSE') {
    const mw = h * 0.7;
    const mh = h;
    const x0 = -mw / 2;
    const y0 = -mh / 2;
    const split = y0 + mh * 0.44;
    roundRect(g, x0, y0, mw, mh, mw * 0.48, { c: KEY_FILL, a: 1 });
    // Highlighted button.
    g.fillStyle(COLORS.uiText, 1);
    if (id === 'LMB' || id === 'RMB') {
      const left = id === 'LMB';
      g.beginPath();
      if (left) {
        g.moveTo(0, split);
        g.lineTo(x0, split);
        g.arc(0, y0 + mw * 0.48, mw / 2, Math.PI, Math.PI * 1.5, false);
        g.lineTo(0, split);
      } else {
        g.moveTo(0, split);
        g.lineTo(0, y0);
        g.arc(0, y0 + mw * 0.48, mw / 2, Math.PI * 1.5, 0, false);
        g.lineTo(-x0, split);
      }
      g.closePath();
      g.fillPath();
    } else if (id === 'MMB' || id === 'WHEEL') {
      g.fillRoundedRect(-mw * 0.1, y0 + mh * 0.14, mw * 0.2, mh * 0.24, mw * 0.08);
    }
    g.lineStyle(1.5, KEY_EDGE, 1);
    g.strokeRoundedRect(x0, y0, mw, mh, mw * 0.48);
    g.lineBetween(x0 + 1, split, -x0 - 1, split);
    g.lineBetween(0, y0 + 1, 0, split);
    if (id === 'WHEEL') {
      // Tiny scroll arrows beside the mouse.
      g.fillStyle(COLORS.uiText, 1);
      g.fillTriangle(mw * 0.62, -h * 0.12, mw * 0.82, -h * 0.12, mw * 0.72, -h * 0.3);
      g.fillTriangle(mw * 0.62, h * 0.12, mw * 0.82, h * 0.12, mw * 0.72, h * 0.3);
      w = mw * 1.9;
      g.x = -mw * 0.45;
    } else {
      w = mw;
    }
  } else {
    // Keyboard keycap.
    const text = String(id);
    const fontSize = text.length <= 1 ? h * 0.5 : text.length <= 3 ? h * 0.4 : h * 0.36;
    const t = label(scene, text, fontSize, COLORS.uiText);
    w = Math.max(h, t.width + h * 0.55);
    const x0 = -w / 2;
    const y0 = -h / 2;
    g.fillStyle(0x0b0f15, 1);
    g.fillRoundedRect(x0, y0 + 2, w, h, h * 0.18);
    g.fillStyle(KEY_FILL, 1);
    g.fillRoundedRect(x0, y0, w, h - 2, h * 0.18);
    g.lineStyle(1.5, KEY_EDGE, 0.95);
    g.strokeRoundedRect(x0, y0, w, h - 2, h * 0.18);
    g.lineStyle(1, 0xffffff, 0.08);
    g.lineBetween(x0 + h * 0.2, y0 + 2.5, -x0 - h * 0.2, y0 + 2.5);
    t.setY(-1);
    c.add(t);
  }
  c.setSize(w, h);
  return c;
}

/** Several glyphs side by side ("A" "D", or "/"-separated alternatives). Returns the container and its width. */
function glyphRow(scene: Phaser.Scene, ids: GlyphId[], size: number): Phaser.GameObjects.Container {
  const row = scene.add.container(0, 0);
  let x = 0;
  const gap = size * 0.18;
  ids.forEach((id, i) => {
    const g = createGlyph(scene, id, size);
    g.x = x + g.width / 2;
    row.add(g);
    x += g.width + (i < ids.length - 1 ? gap : 0);
  });
  row.setSize(x, size);
  return row;
}

export interface ActionHintOptions {
  size?: number;
  fontSize?: number;
  color?: number;
  /** Label colour while disabled. */
  dim?: boolean;
}

/**
 * Glyph + label ("[E] EQUIP"). Origin: left-centre. Rebuilds its glyphs when the
 * input device changes; unsubscribes automatically when destroyed.
 */
export class ActionHint extends Phaser.GameObjects.Container {
  private glyphs: Phaser.GameObjects.Container | null = null;
  private readonly text: Phaser.GameObjects.Text;
  private readonly size: number;
  private offMode: () => void;
  private actionOrIds: UiAction | GlyphId[];
  /** Notified after a relayout so a HintBar can re-align. */
  onResize?: () => void;

  constructor(scene: Phaser.Scene, action: UiAction | GlyphId[], labelText: string, opts: ActionHintOptions = {}) {
    super(scene, 0, 0);
    this.size = opts.size ?? 30;
    this.actionOrIds = action;
    this.text = scene.add
      .text(0, 0, labelText.toUpperCase(), {
        fontFamily: FONT.display,
        fontSize: `${opts.fontSize ?? Math.round(this.size * 0.68)}px`,
        fontStyle: '600',
        color: css(opts.dim ? COLORS.uiDim : (opts.color ?? COLORS.uiText)),
      })
      .setOrigin(0, 0.5);
    this.text.setLetterSpacing(1.5);
    this.add(this.text);
    scene.add.existing(this);
    this.rebuild();
    this.offMode = onInputModeChange(() => this.rebuild());
    this.once(Phaser.GameObjects.Events.DESTROY, () => this.offMode());
  }

  setLabel(s: string): this {
    this.text.setText(s.toUpperCase());
    this.layout();
    return this;
  }

  setLabelColor(color: number): this {
    this.text.setColor(css(color));
    return this;
  }

  setAction(action: UiAction | GlyphId[]): this {
    this.actionOrIds = action;
    this.rebuild();
    return this;
  }

  private rebuild(): void {
    if (!this.scene) return;
    this.glyphs?.destroy();
    const ids = Array.isArray(this.actionOrIds) ? this.actionOrIds : glyphsFor(this.actionOrIds);
    this.glyphs = glyphRow(this.scene, ids, this.size);
    this.addAt(this.glyphs, 0);
    this.layout();
  }

  private layout(): void {
    const gw = this.glyphs?.width ?? 0;
    this.text.x = gw + this.size * 0.32;
    this.setSize(this.text.x + this.text.width, this.size);
    this.onResize?.();
  }
}

/** A row of action hints (footer). Origin depends on align: left/centre/right edge at (x, y). */
export class HintBar extends Phaser.GameObjects.Container {
  private hints: ActionHint[] = [];
  private readonly gap: number;
  private readonly align: 'left' | 'center' | 'right';

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    entries: { action: UiAction | GlyphId[]; label: string; color?: number }[],
    opts: { align?: 'left' | 'center' | 'right'; size?: number; gap?: number; fontSize?: number } = {},
  ) {
    super(scene, x, y);
    this.align = opts.align ?? 'left';
    this.gap = opts.gap ?? 34;
    scene.add.existing(this);
    for (const e of entries) {
      const h = new ActionHint(scene, e.action, e.label, { size: opts.size ?? 30, fontSize: opts.fontSize, color: e.color });
      h.onResize = () => this.layout();
      this.hints.push(h);
      this.add(h);
    }
    this.layout();
  }

  get hintList(): readonly ActionHint[] {
    return this.hints;
  }

  layout(): void {
    let total = 0;
    this.hints.forEach((h, i) => (total += h.width + (i ? this.gap : 0)));
    let x = this.align === 'left' ? 0 : this.align === 'center' ? -total / 2 : -total;
    for (const h of this.hints) {
      h.x = x;
      x += h.width + this.gap;
    }
    this.setSize(total, 30);
  }
}
